import { useState, useEffect, useCallback } from "react";
import { loadPharmacyQueue, savePharmacyDispensing } from "./data.js";
import { jakartaDateKey } from "./lib/format.js";

/* ── helpers ────────────────────────────────────────────────────────────────── */
const STATUS_LABEL = {
  PENDING: "Menunggu",
  DISPENSED: "Diserahkan",
  CANCELLED: "Dibatalkan",
};
const STATUS_COLOR = {
  PENDING: "#d97706",
  DISPENSED: "#16a34a",
  CANCELLED: "#dc2626",
};
const SOURCE_LABEL = { DOKTER_UMUM: "Poli Umum", DENTAL: "Poli Gigi" };

function fmtCurrency(n) {
  if (!n) return "Rp 0";
  return "Rp " + Number(n).toLocaleString("id-ID");
}

/* ── DispensingModal ────────────────────────────────────────────────────────── */
function DispensingModal({ row, identity, onClose, onSaved }) {
  const baseItems = (row.dispensing_items?.length
    ? row.dispensing_items
    : row.prescriptions || []
  ).map((p) => ({
    drug_name: p.drug_name || "",
    dose: p.dose || "",
    frequency: p.frequency || "",
    duration_days: p.duration_days || "",
    route: p.route || "",
    qty_prescribed: p.qty_prescribed || p.qty || 0,
    qty_dispensed: p.qty_dispensed ?? p.qty_prescribed ?? p.qty ?? 0,
    unit_price: p.unit_price || 0,
    subtotal: p.subtotal || 0,
    notes: p.notes || "",
  }));

  const [items, setItems] = useState(baseItems);
  const [notes, setNotes] = useState(row.dispensing_notes || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const updateItem = (idx, field, val) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const next = { ...it, [field]: val };
        if (field === "qty_dispensed" || field === "unit_price") {
          next.subtotal =
            Number(next.qty_dispensed || 0) * Number(next.unit_price || 0);
        }
        return next;
      })
    );
  };

  const total = items.reduce((s, it) => s + Number(it.subtotal || 0), 0);

  const handleSave = async (finalize) => {
    setSaving(true);
    setErr(null);
    try {
      const apotekerId = identity?.staffId || null;
      await savePharmacyDispensing(
        row.visit_id,
        apotekerId,
        items,
        notes,
        finalize
      );
      onSaved();
    } catch (e) {
      setErr(e.message || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-box dispensing-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <strong>{row.client_name}</strong>
            <span className="modal-sub">
              {row.client_code} · {SOURCE_LABEL[row.source_type] || row.source_type}
            </span>
          </div>
          <button className="btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="disp-section-label">Obat Resep</div>
        <div className="disp-table-wrap">
          <table className="disp-table">
            <thead>
              <tr>
                <th>Nama Obat</th>
                <th>Dosis</th>
                <th>Aturan</th>
                <th>Rute</th>
                <th style={{ width: 64 }}>Diresepkan</th>
                <th style={{ width: 72 }}>Diserahkan</th>
                <th style={{ width: 110 }}>Harga Satuan</th>
                <th style={{ width: 110 }}>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx}>
                  <td>
                    <div className="drug-name">{it.drug_name}</div>
                    {it.notes && (
                      <div className="drug-note">{it.notes}</div>
                    )}
                  </td>
                  <td>{it.dose || "—"}</td>
                  <td>
                    {it.frequency || "—"}
                    {it.duration_days ? `, ${it.duration_days} hr` : ""}
                  </td>
                  <td>{it.route || "—"}</td>
                  <td style={{ textAlign: "center" }}>{it.qty_prescribed}</td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      max={it.qty_prescribed}
                      className="disp-qty-input"
                      value={it.qty_dispensed}
                      onChange={(e) =>
                        updateItem(idx, "qty_dispensed", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      className="disp-price-input"
                      placeholder="0"
                      value={it.unit_price || ""}
                      onChange={(e) =>
                        updateItem(idx, "unit_price", e.target.value)
                      }
                    />
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {fmtCurrency(it.subtotal)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={7} style={{ textAlign: "right", fontWeight: 600 }}>
                  Total
                </td>
                <td style={{ textAlign: "right", fontWeight: 700, color: "#0369a1" }}>
                  {fmtCurrency(total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="disp-notes-row">
          <label>Catatan Apoteker</label>
          <input
            type="text"
            className="disp-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Catatan khusus (opsional)"
          />
        </div>

        {err && <div className="form-error">{err}</div>}

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={() => handleSave(false)}
            disabled={saving}
          >
            Simpan Draft
          </button>
          <button
            className="btn btn-primary"
            onClick={() => handleSave(true)}
            disabled={saving}
          >
            {saving ? "Menyimpan…" : "Serahkan ke Kasir"}
          </button>
        </div>
      </div>

      <style>{`
        .dispensing-modal {
          width: 900px;
          max-width: 96vw;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-sub {
          display: block;
          font-size: 12px;
          color: #6b7280;
          margin-top: 2px;
        }
        .disp-section-label {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: .06em;
          color: #6b7280;
          margin: 16px 0 8px;
        }
        .disp-table-wrap { overflow-x: auto; }
        .disp-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }
        .disp-table th {
          background: #f8fafc;
          padding: 7px 10px;
          text-align: left;
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          border-bottom: 1px solid #e5e7eb;
        }
        .disp-table td {
          padding: 8px 10px;
          border-bottom: 1px solid #f3f4f6;
          vertical-align: middle;
        }
        .disp-table tfoot td {
          border-top: 2px solid #e5e7eb;
          border-bottom: none;
          padding: 10px;
        }
        .drug-name { font-weight: 500; }
        .drug-note { font-size: 11px; color: #9ca3af; margin-top: 2px; }
        .disp-qty-input,
        .disp-price-input {
          width: 100%;
          padding: 5px 7px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 13px;
          text-align: right;
        }
        .disp-qty-input:focus,
        .disp-price-input:focus {
          outline: none;
          border-color: #0369a1;
          box-shadow: 0 0 0 2px rgba(3,105,161,.15);
        }
        .disp-notes-row {
          display: flex;
          gap: 10px;
          align-items: center;
          margin-top: 14px;
        }
        .disp-notes-row label {
          font-size: 12px;
          font-weight: 600;
          white-space: nowrap;
          color: #374151;
        }
        .disp-notes-input {
          flex: 1;
          padding: 7px 10px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 13px;
        }
        .disp-notes-input:focus {
          outline: none;
          border-color: #0369a1;
          box-shadow: 0 0 0 2px rgba(3,105,161,.15);
        }
      `}</style>
    </div>
  );
}

/* ── FarmasiPage ────────────────────────────────────────────────────────────── */
export default function FarmasiPage({ identity }) {
  const today = jakartaDateKey();
  const [date, setDate] = useState(today);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);   // row being dispensed
  const [filter, setFilter] = useState("ALL");      // ALL | PENDING | DISPENSED

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await loadPharmacyQueue(date);
      setQueue(data?.rows || []);
    } catch (e) {
      setErr(e.message || "Gagal memuat antrian farmasi");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const filtered = queue.filter(
    (r) =>
      filter === "ALL" ||
      (filter === "PENDING" && (!r.dispensing_status || r.dispensing_status === "PENDING")) ||
      (filter === "DISPENSED" && r.dispensing_status === "DISPENSED")
  );

  const pendingCount = queue.filter(
    (r) => !r.dispensing_status || r.dispensing_status === "PENDING"
  ).length;

  return (
    <div className="page-content">
      <style>{`
        .farmasi-toolbar {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }
        .farmasi-title {
          font-size: 18px;
          font-weight: 700;
          color: #0c4a6e;
          flex: 1;
        }
        .farmasi-badge {
          background: #0369a1;
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          border-radius: 999px;
          padding: 2px 9px;
          margin-left: 6px;
        }
        .farmasi-date-input {
          padding: 6px 10px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 13px;
          cursor: pointer;
        }
        .farmasi-filter-bar {
          display: flex;
          gap: 6px;
        }
        .farmasi-filter-btn {
          padding: 5px 14px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          background: #fff;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          color: #374151;
          transition: all .15s;
        }
        .farmasi-filter-btn.active {
          background: #0369a1;
          border-color: #0369a1;
          color: #fff;
        }
        .farmasi-cards {
          display: grid;
          gap: 12px;
        }
        .farmasi-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 16px 20px;
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 12px 20px;
          align-items: center;
          box-shadow: 0 1px 3px rgba(0,0,0,.06);
        }
        .farmasi-card:hover { border-color: #bae6fd; }
        .fc-top {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .fc-client {
          font-size: 15px;
          font-weight: 700;
          color: #0c4a6e;
        }
        .fc-code {
          font-size: 12px;
          color: #6b7280;
          background: #f3f4f6;
          padding: 2px 8px;
          border-radius: 6px;
        }
        .fc-source {
          font-size: 11px;
          font-weight: 600;
          background: #eff6ff;
          color: #1d4ed8;
          padding: 2px 8px;
          border-radius: 6px;
        }
        .fc-status-badge {
          font-size: 11px;
          font-weight: 700;
          padding: 2px 10px;
          border-radius: 999px;
        }
        .fc-meta {
          font-size: 12px;
          color: #6b7280;
          margin-top: 6px;
        }
        .fc-drugs {
          margin-top: 8px;
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .fc-drug-pill {
          font-size: 11px;
          background: #f0f9ff;
          color: #0369a1;
          border: 1px solid #bae6fd;
          padding: 2px 8px;
          border-radius: 6px;
        }
        .fc-actions {
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: flex-end;
        }
        .btn-dispense {
          padding: 8px 18px;
          background: #0369a1;
          color: #fff;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
        }
        .btn-dispense:hover { background: #0284c7; }
        .btn-dispense.done {
          background: #f3f4f6;
          color: #6b7280;
          cursor: default;
        }
        .farmasi-empty {
          text-align: center;
          color: #9ca3af;
          padding: 60px 0;
          font-size: 14px;
        }
        .farmasi-empty-icon {
          font-size: 40px;
          margin-bottom: 12px;
        }
        .form-error {
          color: #dc2626;
          font-size: 13px;
          margin-top: 8px;
        }
      `}</style>

      {/* toolbar */}
      <div className="farmasi-toolbar">
        <div className="farmasi-title">
          Farmasi / Apotik
          {pendingCount > 0 && (
            <span className="farmasi-badge">{pendingCount} menunggu</span>
          )}
        </div>
        <input
          type="date"
          className="farmasi-date-input"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <div className="farmasi-filter-bar">
          {["ALL", "PENDING", "DISPENSED"].map((f) => (
            <button
              key={f}
              className={`farmasi-filter-btn${filter === f ? " active" : ""}`}
              onClick={() => setFilter(f)}
            >
              {f === "ALL" ? "Semua" : f === "PENDING" ? "Menunggu" : "Selesai"}
            </button>
          ))}
        </div>
        <button
          className="btn btn-secondary"
          onClick={load}
          disabled={loading}
        >
          {loading ? "Memuat…" : "↻ Refresh"}
        </button>
      </div>

      {err && <div className="form-error">{err}</div>}

      {loading ? (
        <div className="farmasi-empty">
          <div className="farmasi-empty-icon">⌛</div>
          Memuat antrian resep…
        </div>
      ) : filtered.length === 0 ? (
        <div className="farmasi-empty">
          <div className="farmasi-empty-icon">💊</div>
          Tidak ada resep{filter !== "ALL" ? ` berstatus ${filter}` : ""} pada tanggal ini.
        </div>
      ) : (
        <div className="farmasi-cards">
          {filtered.map((row) => {
            const dispensed = row.dispensing_status === "DISPENSED";
            const drugs = row.prescriptions || [];
            return (
              <div className="farmasi-card" key={row.visit_id}>
                {/* info kiri */}
                <div>
                  <div className="fc-top">
                    <span className="fc-client">{row.client_name}</span>
                    <span className="fc-code">{row.client_code}</span>
                    <span className="fc-source">
                      {SOURCE_LABEL[row.source_type] || row.source_type}
                    </span>
                    {row.dispensing_status && (
                      <span
                        className="fc-status-badge"
                        style={{
                          background:
                            (STATUS_COLOR[row.dispensing_status] || "#6b7280") + "20",
                          color:
                            STATUS_COLOR[row.dispensing_status] || "#6b7280",
                        }}
                      >
                        {STATUS_LABEL[row.dispensing_status] || row.dispensing_status}
                      </span>
                    )}
                  </div>
                  <div className="fc-meta">
                    Kunjungan {row.visit_number} · {row.doctor_name || "—"}
                  </div>
                  {drugs.length > 0 && (
                    <div className="fc-drugs">
                      {drugs.map((d, i) => (
                        <span key={i} className="fc-drug-pill">
                          {d.drug_name}
                          {d.dose ? ` ${d.dose}` : ""}
                          {d.qty ? ` ×${d.qty}` : ""}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* aksi kanan */}
                <div className="fc-actions">
                  <button
                    className={`btn-dispense${dispensed ? " done" : ""}`}
                    onClick={() => !dispensed && setSelected(row)}
                  >
                    {dispensed ? "✓ Sudah Diserahkan" : "Proses Resep"}
                  </button>
                  {dispensed && (
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize: 12, padding: "4px 10px" }}
                      onClick={() => setSelected(row)}
                    >
                      Lihat Detail
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <DispensingModal
          row={selected}
          identity={identity}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </div>
  );
}
